'use client'

import React, { useEffect, useState } from 'react'
import GenericCTA from '@/src/components/commons/generics/GenericCTA/GenericCTA'
import Image from 'next/image'

// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { A11y, EffectFade, Keyboard, Pagination } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/react'
import 'swiper/css'
import 'swiper/css/effect-fade'
import 'swiper/css/pagination'
import '@/src/styles/overrides/swiper_overrides.scss'

import { HeroSliderProps } from './HeroSlider.interface'
import styles from './HeroSlider.module.scss'

// Import custom icons/assets
import SwiperArrow from '@/public/assets/images/icon_swiper_arrow_prev_white.svg'

import { Swiper as SwiperClass } from 'swiper/types'

const HeroSlider: React.FC<HeroSliderProps> = ({ data }) => {
  const [mainSwiper, setMainSwiper] = useState<SwiperClass>()
  const [thumbSwiper, setThumbSwiper] = useState<SwiperClass>()
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0)

  // Calculate progress bar width & moves currentProgress to position
  const totalSlides = data.length
  const progressWidth = totalSlides > 0 ? 100 / totalSlides : 0
  const progressTransform = currentSlideIndex * progressWidth

  const handlePaginationClick = (index: number) => {
    if (mainSwiper && thumbSwiper) {
      mainSwiper.slideTo(index)
    }
  }

  const handleMainSlideChange = (swiper: SwiperClass) => {
    const newIndex = swiper.realIndex
    setCurrentSlideIndex(newIndex)

    // Synch and slide the thumbnail Swiper to the new index on mainSwipper
    if (thumbSwiper) {
      thumbSwiper.slideTo(newIndex)
    }
  }

  const handleArrowLeft = () => {
    mainSwiper?.slidePrev()
  }

  const handleArrowRight = () => {
    mainSwiper?.slideNext()
  }

  const formatSlideNumber = (n: number) => n.toString().padStart(2, '0')
  const isFirstSlide = currentSlideIndex === 0
  const isLastSlide = currentSlideIndex === data.length - 1

  useEffect(() => {
    if (mainSwiper) {
      const handleResize = () => {
        mainSwiper.update()
        if (thumbSwiper) {
          thumbSwiper.update()
        }
      }

      window.addEventListener('resize', handleResize)

      // Cleanup the event listener when the component unmounts
      return () => {
        window.removeEventListener('resize', handleResize)
      }
    }
  }, [mainSwiper, thumbSwiper])

  return (
    <div className={`${styles.heroSlider}`}>
      <div className={`${styles.container}`}>
        <div className={styles.slider}>
          <div className={styles.sliderContainer}>
            {/* Main Hero Slider */}
            <div className={styles.mainSwiperWrapper}>
              <Swiper
                onInit={setMainSwiper}
                onSlideChange={handleMainSlideChange}
                slidesPerView={1}
                speed={1200}
                initialSlide={0}
                loop={false}
                effect="fade"
                allowTouchMove={data.length > 1}
              >
                {data.map((slide, index) => (
                  <SwiperSlide key={index}>
                    <div className={styles.slideContent}>
                      <div className={styles.textColumn}>
                        <span className={styles.category}>{slide.category}</span>
                        <h1 className={styles.headline}>
                          <span className={styles.markerWrapper}>
                            <span className={styles.markerInner}>
                              <span className={styles.markerInnerOffset}>{slide.headline}</span>
                            </span>
                          </span>
                        </h1>
                        <p className={styles.description}>{slide.description}</p>
                        {/* <span className={styles.date}>{slide.date}</span> */}
                        {slide.ctaUrl && slide.cta && (
                          <GenericCTA
                            ctaText={slide.cta}
                            ctaURL={slide.ctaUrl} // Set to # or empty string to make it non-navigational
                            variant="red-outline"
                            className={styles.cta}
                          />
                        )}
                      </div>
                      <div className={`${styles.imageColumn} hidden md:block lg:hidden`}>
                        <Image
                          src={slide.imageTablet}
                          alt={slide.headline}
                          layout="fill"
                          objectFit="contain"
                          priority={index === 0}
                        />
                      </div>
                      <div className={`${styles.imageColumn} hidden lg:block`}>
                        <Image
                          src={slide.imageDesktop}
                          alt={slide.headline}
                          layout="fill"
                          objectFit="contain"
                          priority={index === 0}
                        />
                      </div>
                    </div>
                  </SwiperSlide>
                ))}
              </Swiper>
            </div>

            {/* Thumbnail Swiper */}
            {data.length > 1 && (
              <>
                <div className={`${styles.thumbnailSwiperWrapper} container`}>
                  <Swiper
                    onInit={setThumbSwiper}
                    slidesPerView="auto"
                    spaceBetween={20}
                    initialSlide={0}
                    loop={false}
                    allowTouchMove
                    // property for stability with 'auto' and 'snap' logic
                    watchSlidesProgress={true}
                    className={styles.thumbnailSwiper}
                  >
                    {data.map((slide, index) => (
                      <SwiperSlide
                        key={index}
                        onClick={() => handlePaginationClick(index)}
                        className={styles.thumbnailSlide}
                      >
                        <div className={styles.thumbTile}>
                          <div className={styles.thumbImageWrapper}>
                            <Image
                              src={slide.thumb}
                              alt={slide.headline}
                              layout="fill"
                              objectFit="contain"
                            />
                          </div>
                          <div className={styles.thumbnailContent}>
                            <div className={`${styles.thumbnailCategory} uppercase`}>
                              {slide.category}
                            </div>
                            <h2 className={styles.thumbnailTitle}>{slide.headline}</h2>
                          </div>
                        </div>
                      </SwiperSlide>
                    ))}
                  </Swiper>
                </div>
              </>
            )}
          </div>
        </div>
        {/* Navigation Controls */}
        {data.length > 1 && (
          <div className="flex w-full flex-col lg:px-5">
            <div aria-hidden={true} className={styles.progressBar}>
              <div
                className={styles.currentProgress}
                style={{
                  width: `${progressWidth}%`,
                  left: `${progressTransform}%`,
                }}
              ></div>
            </div>
            <div className={styles.controls}>
              <div className={styles.pagination}>
                <span data-testid="hero-pager">
                  <span className={styles.activePage}>
                    {formatSlideNumber(currentSlideIndex + 1)}
                  </span>
                  /{formatSlideNumber(data.length)}
                </span>
              </div>
              <div className={styles.navigation}>
                <button
                  onClick={handleArrowLeft}
                  className={`${styles.navButton} ${isFirstSlide ? styles.disabled : ''}`}
                >
                  <Image
                    src={SwiperArrow}
                    className={styles.prevArrow}
                    alt="Previous Slide"
                    style={{ opacity: isFirstSlide ? 0.2 : 1 }}
                  />
                </button>
                <button
                  onClick={handleArrowRight}
                  className={`${styles.navButton} ${isLastSlide ? styles.disabled : ''}`}
                >
                  <Image
                    src={SwiperArrow}
                    className={styles.nextArrow}
                    alt="Next Slide"
                    style={{ opacity: isLastSlide ? 0.2 : 1 }}
                  />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default HeroSlider
